import { KeyboardEvent, useEffect, useState } from 'react';

export function useListNavigation(
  onCancel: () => void,
  onSelect: (index: number, closeSelect?: boolean) => void,
  numberOfItems: number,
  selectOnArrowMovement?: boolean,
) {
  const [isActive, setIsActive] = useState(false);

  const [selectedItemIndex, setSelectedItemIndex] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (selectOnArrowMovement && selectedItemIndex !== undefined) {
      onSelect(selectedItemIndex, false);
    }
  }, [selectedItemIndex]);

  const onItemClick = (index: number) => {
    if (onSelect) {
      onSelect(index);
    }
    setIsActive(false);
    setSelectedItemIndex(undefined);
  };

  const handleCancel = () => {
    setIsActive(false);
    if (onCancel) {
      onCancel();
      setSelectedItemIndex(undefined);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      handleCancel();
    }

    if (!numberOfItems) return;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }

    if (e.key === 'ArrowDown' && !isActive && numberOfItems > 0) {
      setIsActive(true);
    }

    if (e.key === 'Enter' && onSelect) {
      e.preventDefault();
      onItemClick(selectedItemIndex);
    }

    const highestIndex = numberOfItems - 1;
    const itemIndexIsUndefined = typeof selectedItemIndex === 'undefined';

    if (e.key === 'ArrowUp') {
      if (itemIndexIsUndefined) {
        setSelectedItemIndex(highestIndex);
        return;
      }

      if (selectedItemIndex === 0) {
        setSelectedItemIndex(highestIndex);
        return;
      }

      setSelectedItemIndex((prev) => prev - 1);
    }

    if (e.key === 'ArrowDown') {
      if (itemIndexIsUndefined) {
        setSelectedItemIndex(0);
        return;
      }

      if (selectedItemIndex === highestIndex) {
        setSelectedItemIndex(0);
        return;
      }

      setSelectedItemIndex((prev) => prev + 1);
    }
  };

  return {
    selectedItemIndex,
    setSelectedItemIndex,
    onItemClick,
    handleKeyDown,
    isActive,
    setIsActive,
    handleCancel,
  };
}
